import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Space, 
  Spin, 
  Alert, 
  Collapse,
  Row,
  Col,
  Tooltip,
  message,
  Tabs,
  Input
} from 'antd';
import { 
  ArrowLeftOutlined, 
  PrinterOutlined, 
  InfoCircleOutlined,
  ReloadOutlined,
  CodeOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useTemplate } from '../../hooks/templateHooks';
import printTemplate from '../../utils/printExpTemplate';
import TemplateDesigner from './TemplateDesigner';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

// Default variables example
const DEFAULT_VARIABLES = {
  "customer_name": "John Doe",
  "invoice_number": "INV-2023-001",
  "total_amount": "$1,250.00",
  "ref_exp": "123456",
  "date": "03/06/2025"
};

const TemplatePrintPreview = ({ templateId, onBack }) => {
  const { template, loading, error, fetchTemplate } = useTemplate(templateId);
  
  const [variablesJson, setVariablesJson] = useState(JSON.stringify(DEFAULT_VARIABLES, null, 2));
  const [parsedVariables, setParsedVariables] = useState(DEFAULT_VARIABLES);
  const [jsonError, setJsonError] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');
  
  // Parse the JSON variables
  useEffect(() => {
    try {
      const parsed = JSON.parse(variablesJson);
      setParsedVariables(parsed);
      setJsonError(null);
    } catch (err) {
      setJsonError(err.message);
    }
  }, [variablesJson]);
  
  // Generate print preview
  const handlePrintPreview = () => {
    if (jsonError) {
      message.error('Please fix the JSON errors before printing');
      return;
    }
    
    try {
      // Validate template data
      if (!template || !template.data || !Array.isArray(template.data)) {
        message.error('Invalid template data');
        return;
      }
      
      // Print the template with the provided variables
      printTemplate(template.data, parsedVariables);
    } catch (err) {
      message.error(`Error generating print preview: ${err.message}`);
    }
  };
  
  // Handle JSON update
  const handleJsonChange = (value) => {
    setVariablesJson(value);
  };
  
  // Reset to default variables
  const handleResetVariables = () => {
    setVariablesJson(JSON.stringify(DEFAULT_VARIABLES, null, 2));
  };
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" tip="Loading template..." />
      </div>
    );
  }
  
  if (error) {
    return (
      <Alert 
        message="Error Loading Template" 
        description={error} 
        type="error" 
        showIcon 
        action={
          <Space>
            <PermissionButton   size="small" onClick={onBack}>
              Back to Editor
            </PermissionButton>
            <Button size="small" type="primary" onClick={() => fetchTemplate()}>
              Retry
            </Button>
          </Space>
        }
      />
    );
  }
  
  if (!template) {
    return (
      <Alert 
        message="Template Not Found" 
        description="The requested template could not be found." 
        type="error" 
        showIcon 
        action={
          <Button size="small" onClick={onBack}>
            Back to Templates
          </Button>
        }
      />
    );
  }
  
  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Print Preview: {template.name}
            </Title>
            <Text type="secondary">
              Preview how your template will look with dynamic variables
            </Text>
          </div>
          
          <Space>
            <PermissionButton
              icon={<ArrowLeftOutlined />} 
              onClick={onBack}
            >
              Back to Editor
            </PermissionButton>
            
            <PermissionButton permissionModule="templates" permissionAction="print"
              type="primary" 
              icon={<PrinterOutlined />} 
              onClick={handlePrintPreview}
              disabled={!!jsonError}
            >
              Print Template
            </PermissionButton>
          </Space>
        </div>
        
        <Row gutter={24}>
          <Col span={10}>
            <Card 
              title={
                <Space>
                  <CodeOutlined />
                  Variable Data
                  <Tooltip title="Enter JSON data to replace variables in your template. Use the same keys as your placeholders.">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </Space>
              }
              extra={
                <Button 
                  icon={<ReloadOutlined />} 
                  size="small" 
                  onClick={handleResetVariables}
                >
                  Reset
                </Button>
              }
              style={{ marginBottom: '24px' }}
            >
              <Alert
                message="Template Variables"
                description="Enter JSON data to replace the variables in your template. Variable names should match the placeholders used in your template."
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
              />
              
              <div style={{ marginBottom: '16px' }}>
                <Input.TextArea
                  value={variablesJson}
                  placeholder="Enter JSON here"
                  onChange={(e) => handleJsonChange(e.target.value)}
                  autoSize={{ minRows: 10, maxRows: 20 }}
                  style={{
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    border: jsonError ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
                  }}
                />
                
                {jsonError && (
                  <Alert 
                    message="JSON Error" 
                    description={jsonError} 
                    type="error" 
                    showIcon 
                    style={{ marginTop: '8px' }} 
                  />
                )}
              </div>
              
              <Collapse ghost>
                <Panel header="Available Variables" key="1">
                  <Paragraph>
                    Your template uses variables in the format <code>&#123;&#123;variable_name&#125;&#125;</code>. 
                    Make sure your JSON includes all the variables used in your template.
                  </Paragraph>
                  
                  <div style={{ 
                    backgroundColor: '#f5f5f5', 
                    padding: '12px', 
                    borderRadius: '4px',
                    marginBottom: '16px'
                  }}>
                    <Text strong>Template Variables:</Text>
                    <ul style={{ margin: '8px 0', paddingLeft: '24px' }}>
                      {Array.from(new Set(
                        template.data
                          .map(el => el.text || '')
                          .join(' ')
                          .match(/\{\{([^}]+)\}\}/g) || []
                      )).map((variable, index) => (
                        <li key={index}>
                          <code>{variable}</code>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Panel>
              </Collapse>
            </Card>
          </Col>
          
          <Col span={14}>
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab}
              type="card"
              style={{ marginBottom: '16px' }}
            >
              <TabPane 
                tab={
                  <span>
                    <EyeOutlined />
                    Preview
                  </span>
                } 
                key="preview"
              >
                <Card
                  title={
                    <Space>
                      <EyeOutlined />
                      Template Preview
                    </Space>
                  }
                  bodyStyle={{ padding: 0 }}
                >
                  <div style={{ 
                    backgroundColor: '#f8f8f8',
                    border: '1px solid #e8e8e8',
                    borderRadius: '2px',
                    padding: '12px'
                  }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '2px', position: 'relative' }}>
                      <TemplateDesigner
                        initialElements={template.data}
                        readOnly={true}
                        onChange={() => {}}
                      />
                    </div>
                  </div>
                </Card>
              </TabPane>
              
              <TabPane 
                tab={
                  <span>
                    <CodeOutlined />
                    Template JSON
                  </span>
                } 
                key="json"
              >
                <Card
                  title={
                    <Space>
                      <CodeOutlined />
                      Template Definition
                    </Space>
                  }
                >
                  <div style={{ maxHeight: '600px', overflow: 'auto' }}>
                    <Input.TextArea
                      value={JSON.stringify(template.data, null, 2)}
                      readOnly
                      autoSize={{ minRows: 10, maxRows: 20 }}
                      style={{
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        backgroundColor: '#f5f5f5'
                      }}
                    />
                  </div>
                </Card>
              </TabPane>
            </Tabs>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default TemplatePrintPreview;